import { apiService } from './api';
import {
  ComprehensiveAnalysis,
  AnalysisHistoryItem,
  BatchAnalysisRequest,
  BatchAnalysisResult,
  MarketDataCoverage,
  TimeframeConfig,
} from '../types';

class ComprehensiveAnalysisService {
  private readonly timeframeConfigs: TimeframeConfig[] = [
    {
      timeframe: '1m',
      period: 'intraday',
      lookbackDays: 1,
      description: 'Intraday (1-minute) - For day trading and scalping',
    },
    {
      timeframe: '5m',
      period: 'intraday',
      lookbackDays: 3,
      description:
        'Short-term intraday (5-minute) - For swing trades within a day',
    },
    {
      timeframe: '15m',
      period: 'short_term',
      lookbackDays: 7,
      description: 'Short-term (15-minute) - For quick momentum trades',
    },
    {
      timeframe: '30m',
      period: 'short_term',
      lookbackDays: 14,
      description: 'Short-term (30-minute) - For short-term position trades',
    },
    {
      timeframe: '1h',
      period: 'swing',
      lookbackDays: 30,
      description: 'Swing trading (1-hour) - For multi-day swing trades',
    },
    {
      timeframe: '4h',
      period: 'mid_term',
      lookbackDays: 90,
      description: 'Mid-term (4-hour) - For weekly to monthly positions',
    },
    {
      timeframe: '1d',
      period: 'mid_term',
      lookbackDays: 180,
      description: 'Daily (1-day) - For medium-term investments',
    },
    {
      timeframe: '1w',
      period: 'long_term',
      lookbackDays: 365,
      description: 'Weekly (1-week) - For long-term investments',
    },
    {
      timeframe: '1M',
      period: 'long_term',
      lookbackDays: 730,
      description: 'Monthly (1-month) - For very long-term positions',
    },
  ];

  /**
   * Get comprehensive analysis for a single symbol
   */
  async getComprehensiveAnalysis(
    symbol: string,
    timeframes?: string[],
    forceRefresh: boolean = false,
  ): Promise<ComprehensiveAnalysis> {
    try {
      return await apiService.getComprehensiveAnalysis(
        symbol,
        timeframes,
        forceRefresh,
      );
    } catch (error: any) {
      console.error(
        `Failed to get comprehensive analysis for ${symbol}:`,
        error,
      );
      throw new Error(
        `Analysis failed for ${symbol}: ${error.response?.data?.message || error.message}`,
      );
    }
  }

  /**
   * Get analysis history for a symbol
   */
  async getAnalysisHistory(
    symbol: string,
    limit: number = 50,
    offset: number = 0,
  ): Promise<AnalysisHistoryItem[]> {
    try {
      return await apiService.getAnalysisHistory(symbol, limit, offset);
    } catch (error: any) {
      console.error(`Failed to get analysis history for ${symbol}:`, error);
      throw new Error(
        `Failed to get history for ${symbol}: ${error.response?.data?.message || error.message}`,
      );
    }
  }

  /**
   * Perform batch analysis on multiple symbols
   */
  async batchAnalysis(
    request: BatchAnalysisRequest,
  ): Promise<BatchAnalysisResult> {
    try {
      return await apiService.batchComprehensiveAnalysis(request);
    } catch (error: any) {
      console.error('Failed to perform batch analysis:', error);
      throw new Error(
        `Batch analysis failed: ${error.response?.data?.message || error.message}`,
      );
    }
  }

  /**
   * Get market data coverage for a symbol
   */
  async getMarketDataCoverage(symbol: string): Promise<MarketDataCoverage> {
    try {
      return await apiService.getMarketDataCoverage(symbol);
    } catch (error: any) {
      console.error(`Failed to get market data coverage for ${symbol}:`, error);
      throw new Error(
        `Coverage check failed for ${symbol}: ${error.response?.data?.message || error.message}`,
      );
    }
  }

  /**
   * Get available timeframe configurations
   */
  getTimeframeConfigs(): TimeframeConfig[] {
    return this.timeframeConfigs;
  }

  /**
   * Get timeframe config by period
   */
  getTimeframeConfigsByPeriod(period: string): TimeframeConfig[] {
    return this.timeframeConfigs.filter((config) => config.period === period);
  }

  /**
   * Get timeframe config by timeframe
   */
  getTimeframeConfig(timeframe: string): TimeframeConfig | undefined {
    return this.timeframeConfigs.find(
      (config) => config.timeframe === timeframe,
    );
  }

  /**
   * Format recommendation for display
   */
  formatRecommendation(action: string, confidence: number): string {
    const confidenceText =
      confidence >= 80 ? 'High' : confidence >= 60 ? 'Medium' : 'Low';
    return `${action} (${confidenceText} Confidence: ${confidence}%)`;
  }

  /**
   * Get color for recommendation
   */
  getRecommendationColor(action: string): string {
    switch (action) {
      case 'STRONG_BUY':
        return '#10B981'; // Green
      case 'BUY':
        return '#34D399'; // Light green
      case 'HOLD':
        return '#F59E0B'; // Yellow
      case 'SELL':
        return '#F87171'; // Light red
      case 'STRONG_SELL':
        return '#EF4444'; // Red
      default:
        return '#6B7280'; // Gray
    }
  }

  /**
   * Get color for signal strength
   */
  getSignalColor(signal: string): string {
    switch (signal) {
      case 'STRONG_BUY':
      case 'BUY':
        return '#10B981'; // Green
      case 'NEUTRAL':
        return '#F59E0B'; // Yellow
      case 'SELL':
      case 'STRONG_SELL':
        return '#EF4444'; // Red
      default:
        return '#6B7280'; // Gray
    }
  }

  /**
   * Get color for confidence level
   */
  getConfidenceColor(confidence: number): string {
    if (confidence >= 80) return '#10B981'; // Green
    if (confidence >= 60) return '#F59E0B'; // Yellow
    return '#EF4444'; // Red
  }

  /**
   * Get color for volatility regime
   */
  getVolatilityColor(regime: string): string {
    switch (regime) {
      case 'LOW':
        return '#10B981'; // Green
      case 'NORMAL':
        return '#3B82F6'; // Blue
      case 'HIGH':
        return '#F59E0B'; // Yellow
      case 'EXTREME':
        return '#EF4444'; // Red
      default:
        return '#6B7280'; // Gray
    }
  }

  /**
   * Get color for trend
   */
  getTrendColor(trend: string): string {
    switch (trend) {
      case 'BULLISH':
        return '#10B981'; // Green
      case 'BEARISH':
        return '#EF4444'; // Red
      case 'SIDEWAYS':
        return '#F59E0B'; // Yellow
      default:
        return '#6B7280'; // Gray
    }
  }

  /**
   * Format price with proper decimal places
   */
  formatPrice(price: number | null | undefined, decimals: number = 2): string {
    if (
      price === null ||
      price === undefined ||
      isNaN(price) ||
      typeof price !== 'number'
    ) {
      return '0.00';
    }
    return price.toFixed(decimals);
  }

  /**
   * Format percentage
   */
  formatPercentage(
    value: number | null | undefined,
    decimals: number = 2,
  ): string {
    if (value === null || value === undefined || isNaN(value)) {
      return '0.00%';
    }
    return `${value.toFixed(decimals)}%`;
  }

  /**
   * Format large numbers (market cap, volume, etc.)
   */
  formatLargeNumber(value: number | null | undefined): string {
    if (
      value === null ||
      value === undefined ||
      isNaN(value) ||
      typeof value !== 'number'
    ) {
      return '0';
    }
    if (value >= 1e12) return `${(value / 1e12).toFixed(2)}T`;
    if (value >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
    if (value >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
    if (value >= 1e3) return `${(value / 1e3).toFixed(2)}K`;
    return value.toFixed(2);
  }

  /**
   * Get risk level description
   */
  getRiskLevelDescription(riskLevel: string): string {
    switch (riskLevel) {
      case 'LOW':
        return 'Conservative - Suitable for capital preservation';
      case 'MEDIUM':
        return 'Moderate - Balanced risk and reward';
      case 'HIGH':
        return 'Aggressive - Higher potential returns with increased risk';
      default:
        return 'Unknown risk level';
    }
  }

  /**
   * Get time horizon description
   */
  getTimeHorizonDescription(timeHorizon: string): string {
    switch (timeHorizon) {
      case 'intraday':
        return 'Same day (hours to minutes)';
      case 'short_term':
        return 'Days to weeks';
      case 'swing':
        return 'Weeks to months';
      case 'mid_term':
        return 'Months to quarters';
      case 'long_term':
        return 'Quarters to years';
      default:
        return timeHorizon;
    }
  }

  /**
   * Calculate overall score from analysis
   */
  calculateOverallScore(analysis: ComprehensiveAnalysis): number {
    const { overallRecommendation } = analysis;

    // Base score from recommendation action
    let score = 0;
    switch (overallRecommendation.action) {
      case 'STRONG_BUY':
        score = 90;
        break;
      case 'BUY':
        score = 70;
        break;
      case 'HOLD':
        score = 50;
        break;
      case 'SELL':
        score = 30;
        break;
      case 'STRONG_SELL':
        score = 10;
        break;
    }

    // Adjust by confidence
    const confidenceAdjustment = (overallRecommendation.confidence - 50) * 0.4;
    score += confidenceAdjustment;

    // Clamp to 0-100 range
    return Math.max(0, Math.min(100, Math.round(score)));
  }

  /**
   * Get summary statistics from analysis
   */
  getAnalysisSummary(analysis: ComprehensiveAnalysis) {
    const timeframes = Object.keys(analysis.timeframes);
    const buySignals = timeframes.filter((tf) =>
      analysis.timeframes[tf].technical.overallSignal.includes('BUY'),
    ).length;
    const sellSignals = timeframes.filter((tf) =>
      analysis.timeframes[tf].technical.overallSignal.includes('SELL'),
    ).length;
    const neutralSignals = timeframes.filter(
      (tf) => analysis.timeframes[tf].technical.overallSignal === 'NEUTRAL',
    ).length;

    return {
      totalTimeframes: timeframes.length,
      buySignals,
      sellSignals,
      neutralSignals,
      buyPercentage: (buySignals / timeframes.length) * 100,
      sellPercentage: (sellSignals / timeframes.length) * 100,
      neutralPercentage: (neutralSignals / timeframes.length) * 100,
    };
  }

  /**
   * Check if analysis is recent (within last hour)
   */
  isAnalysisRecent(analysis: ComprehensiveAnalysis): boolean {
    const analysisTime = new Date(analysis.timestamp);
    const now = new Date();
    const diffHours =
      (now.getTime() - analysisTime.getTime()) / (1000 * 60 * 60);
    return diffHours < 1;
  }

  /**
   * Get analysis age in human-readable format
   */
  getAnalysisAge(analysis: ComprehensiveAnalysis): string {
    const now = new Date();
    const analysisDate = new Date(analysis.timestamp);
    const diffMs = now.getTime() - analysisDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffMs / (1000 * 60));

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  }

  // ===== NEW HORIZON-SPECIFIC METHODS =====

  /**
   * Analyze a ticker for a specific investment horizon
   */
  async analyzeHorizon(
    symbol: string,
    horizon: '1hr' | '1d' | '1w' | '1m' | '1yr',
    forceRefresh: boolean = false,
  ): Promise<any> {
    try {
      const response = await apiService.analyzeHorizon(
        symbol,
        horizon,
        forceRefresh,
      );
      return response;
    } catch (error: any) {
      console.error(
        `Failed to analyze horizon for ${symbol} (${horizon}):`,
        error,
      );
      throw new Error(
        `Horizon analysis failed for ${symbol} (${horizon}): ${error.response?.data?.message || error.message}`,
      );
    }
  }

  /**
   * Analyze a ticker across all investment horizons (individual requests)
   */
  async analyzeAllHorizons(
    symbol: string,
    forceRefresh: boolean = false,
    progressCallback?: (
      horizon: string,
      status: 'loading' | 'completed' | 'error',
      data?: any,
      error?: string,
    ) => void,
  ): Promise<any> {
    try {
      const horizons: ('1hr' | '1d' | '1w' | '1m' | '1yr')[] = [
        '1hr',
        '1d',
        '1w',
        '1m',
        '1yr',
      ];
      const horizonResults: any = {};
      const errors: string[] = [];

      // Analyze each horizon individually
      for (const horizon of horizons) {
        try {
          console.log(`Analyzing ${symbol} for ${horizon} horizon...`);

          // Notify progress callback that we're starting this horizon
          if (progressCallback) {
            progressCallback(horizon, 'loading');
          }

          const result = await apiService.analyzeHorizon(
            symbol,
            horizon,
            forceRefresh,
          );
          horizonResults[horizon] = result;

          // Notify progress callback that this horizon completed
          if (progressCallback) {
            progressCallback(horizon, 'completed', result);
          }
        } catch (error: any) {
          console.error(
            `Failed to analyze ${horizon} horizon for ${symbol}:`,
            error,
          );
          const errorResult = {
            success: false,
            error: error.response?.data?.message || error.message,
            horizon,
            symbol,
            timestamp: new Date().toISOString(),
          };
          horizonResults[horizon] = errorResult;
          errors.push(
            `${horizon}: ${error.response?.data?.message || error.message}`,
          );

          // Notify progress callback that this horizon failed
          if (progressCallback) {
            progressCallback(
              horizon,
              'error',
              undefined,
              error.response?.data?.message || error.message,
            );
          }
        }
      }

      // Get current market data
      let marketData = {
        currentPrice: 0,
        priceChange: 0,
        priceChangePercent: 0,
        volume: 0,
      };

      try {
        // Try to get market data from the first successful analysis
        const firstSuccessful = Object.values(horizonResults).find(
          (r: any) => r.success && r.analysis,
        ) as any;
        if (firstSuccessful && firstSuccessful.analysis) {
          marketData = {
            currentPrice: firstSuccessful.analysis.currentPrice,
            priceChange: 0, // Would need to calculate from previous data
            priceChangePercent: 0,
            volume: 0,
          };
        }
      } catch (error) {
        console.error('Failed to get market data:', error);
      }

      // Calculate overall recommendation
      const overallRecommendation = this.calculateOverallRecommendation(
        horizonResults,
        marketData,
      );

      return {
        success: true,
        symbol,
        timestamp: new Date().toISOString(),
        horizons: horizonResults,
        overallRecommendation,
        marketData,
        metadata: {
          analysisVersion: '1.0.0',
          dataQuality: this.calculateOverallDataQuality(horizonResults),
          lastUpdated: new Date().toISOString(),
          dataPoints: this.calculateTotalDataPoints(horizonResults),
        },
        errors: errors.length > 0 ? errors : undefined,
      };
    } catch (error: any) {
      console.error(`Failed to analyze all horizons for ${symbol}:`, error);
      throw new Error(
        `Multi-horizon analysis failed for ${symbol}: ${error.response?.data?.message || error.message}`,
      );
    }
  }

  /**
   * Get multi-horizon analysis for a symbol
   */
  async getMultiHorizonAnalysis(symbol: string): Promise<any> {
    try {
      const response = await apiService.getMultiHorizonAnalysis(symbol);
      return response;
    } catch (error: any) {
      console.error(
        `Failed to get multi-horizon analysis for ${symbol}:`,
        error,
      );
      throw new Error(
        `Failed to get multi-horizon analysis for ${symbol}: ${error.response?.data?.message || error.message}`,
      );
    }
  }

  /**
   * Get available investment horizons
   */
  async getAvailableHorizons(): Promise<any> {
    try {
      const response = await apiService.getAvailableHorizons();
      return response;
    } catch (error: any) {
      console.error('Failed to get available horizons:', error);
      throw new Error(
        `Failed to get horizons: ${error.response?.data?.message || error.message}`,
      );
    }
  }

  /**
   * Get horizon configuration by ID
   */
  getHorizonConfig(horizonId: string): any {
    const horizonConfigs = {
      '1hr': {
        id: '1hr',
        name: '1 Hour',
        description: 'Intraday trading analysis',
        dataTimeframe: '5m',
        dataPeriod: '1d',
        useCase: 'Scalping, intraday momentum',
        riskProfile: 'High',
        confidenceAdjustment: -10,
      },
      '1d': {
        id: '1d',
        name: '1 Day',
        description: 'Short-term day trading analysis',
        dataTimeframe: '1h',
        dataPeriod: '1d',
        useCase: 'Day trading, session patterns',
        riskProfile: 'Medium-High',
        confidenceAdjustment: -5,
      },
      '1w': {
        id: '1w',
        name: '1 Week',
        description: 'Weekly swing trading analysis',
        dataTimeframe: '1d',
        dataPeriod: '7d',
        useCase: 'Swing trading, weekly trends',
        riskProfile: 'Medium',
        confidenceAdjustment: 0,
      },
      '1m': {
        id: '1m',
        name: '1 Month',
        description: 'Monthly position trading analysis',
        dataTimeframe: '1d',
        dataPeriod: '30d',
        useCase: 'Position trading, monthly cycles',
        riskProfile: 'Medium-Low',
        confidenceAdjustment: +5,
      },
      '1yr': {
        id: '1yr',
        name: '1 Year',
        description: 'Long-term investment analysis',
        dataTimeframe: '1d',
        dataPeriod: '365d',
        useCase: 'Long-term investing, trend following',
        riskProfile: 'Low',
        confidenceAdjustment: +10,
      },
    };
    return horizonConfigs[horizonId as keyof typeof horizonConfigs];
  }

  /**
   * Get all horizon configurations
   */
  getAllHorizonConfigs(): any[] {
    return [
      this.getHorizonConfig('1hr'),
      this.getHorizonConfig('1d'),
      this.getHorizonConfig('1w'),
      this.getHorizonConfig('1m'),
      this.getHorizonConfig('1yr'),
    ];
  }

  /**
   * Format horizon analysis for display
   */
  formatHorizonAnalysis(analysis: any): any {
    if (!analysis || !analysis.analysis) {
      return null;
    }

    const { analysis: analysisData } = analysis;

    return {
      ...analysis,
      formattedRecommendation: this.formatRecommendation(
        analysisData.recommendation,
        analysisData.confidence,
      ),
      recommendationColor: this.getRecommendationColor(
        analysisData.recommendation,
      ),
      confidenceColor: this.getConfidenceColor(analysisData.confidence),
      currentPrice: this.formatPrice(analysisData.currentPrice),
      supportLevel: this.formatPrice(analysisData.supportLevel),
      resistanceLevel: this.formatPrice(analysisData.resistanceLevel),
      riskLevelDescription: this.getRiskLevelDescription(
        analysisData.riskLevel,
      ),
      dataQualityColor: this.getConfidenceColor(analysisData.dataQuality * 100),
    };
  }

  /**
   * Format multi-horizon analysis for display
   */
  formatMultiHorizonAnalysis(analysis: any): any {
    if (!analysis || !analysis.horizons) {
      return null;
    }

    const formattedHorizons: any = {};

    // Format each horizon analysis
    Object.entries(analysis.horizons).forEach(
      ([horizon, horizonAnalysis]: [string, any]) => {
        if (horizonAnalysis.success && horizonAnalysis.analysis) {
          formattedHorizons[horizon] =
            this.formatHorizonAnalysis(horizonAnalysis);
        } else {
          formattedHorizons[horizon] = horizonAnalysis;
        }
      },
    );

    return {
      ...analysis,
      horizons: formattedHorizons,
      overallRecommendation: {
        ...analysis.overallRecommendation,
        formattedAction: this.formatRecommendation(
          analysis.overallRecommendation.action,
          analysis.overallRecommendation.confidence,
        ),
        actionColor: this.getRecommendationColor(
          analysis.overallRecommendation.action,
        ),
        confidenceColor: this.getConfidenceColor(
          analysis.overallRecommendation.confidence,
        ),
        riskLevelDescription: this.getRiskLevelDescription(
          analysis.overallRecommendation.riskLevel,
        ),
      },
      marketData: {
        ...analysis.marketData,
        currentPrice: this.formatPrice(analysis.marketData.currentPrice),
        priceChange: this.formatPrice(analysis.marketData.priceChange),
        priceChangePercent: this.formatPercentage(
          analysis.marketData.priceChangePercent,
        ),
        volume: this.formatLargeNumber(analysis.marketData.volume),
      },
      metadata: {
        ...analysis.metadata,
        dataQualityColor: this.getConfidenceColor(
          analysis.metadata.dataQuality * 100,
        ),
      },
    };
  }

  /**
   * Get best performing horizon from multi-horizon analysis
   */
  getBestHorizon(analysis: any): string | null {
    if (!analysis || !analysis.horizons) {
      return null;
    }

    let bestHorizon: string | null = null;
    let bestConfidence = 0;

    Object.entries(analysis.horizons).forEach(
      ([horizon, horizonAnalysis]: [string, any]) => {
        if (horizonAnalysis.success && horizonAnalysis.analysis) {
          const confidence = horizonAnalysis.analysis.confidence || 0;
          if (confidence > bestConfidence) {
            bestConfidence = confidence;
            bestHorizon = horizon;
          }
        }
      },
    );

    return bestHorizon;
  }

  /**
   * Get horizon summary statistics
   */
  getHorizonSummary(analysis: any): any {
    if (!analysis || !analysis.horizons) {
      return null;
    }

    const horizons = Object.values(analysis.horizons);
    const successfulHorizons = horizons.filter((h: any) => h.success);
    const failedHorizons = horizons.filter((h: any) => !h.success);

    const recommendations = successfulHorizons
      .map((h: any) => h.analysis?.recommendation)
      .filter(Boolean);
    const buySignals = recommendations.filter((r: string) =>
      r.includes('BUY'),
    ).length;
    const sellSignals = recommendations.filter((r: string) =>
      r.includes('SELL'),
    ).length;
    const holdSignals = recommendations.filter(
      (r: string) => r === 'HOLD',
    ).length;

    const avgConfidence =
      successfulHorizons.length > 0
        ? successfulHorizons.reduce(
            (sum: number, h: any) => sum + (h.analysis?.confidence || 0),
            0,
          ) / successfulHorizons.length
        : 0;

    return {
      totalHorizons: horizons.length,
      successfulHorizons: successfulHorizons.length,
      failedHorizons: failedHorizons.length,
      successRate: (successfulHorizons.length / horizons.length) * 100,
      buySignals,
      sellSignals,
      holdSignals,
      avgConfidence,
      consensus: this.getConsensus(buySignals, sellSignals, holdSignals),
    };
  }

  /**
   * Get consensus from horizon signals
   */
  private getConsensus(
    buySignals: number,
    sellSignals: number,
    holdSignals: number,
  ): string {
    const total = buySignals + sellSignals + holdSignals;
    if (total === 0) return 'NEUTRAL';

    const buyPercentage = (buySignals / total) * 100;
    const sellPercentage = (sellSignals / total) * 100;

    if (buyPercentage >= 60) return 'BULLISH';
    if (sellPercentage >= 60) return 'BEARISH';
    return 'NEUTRAL';
  }

  /**
   * Calculate overall recommendation from horizon results
   */
  private calculateOverallRecommendation(
    horizonResults: any,
    marketData: any,
  ): any {
    const horizonWeights = {
      '1hr': 0.1,
      '1d': 0.2,
      '1w': 0.25,
      '1m': 0.25,
      '1yr': 0.2,
    };

    let totalScore = 0;
    let totalWeight = 0;
    let bestHorizon: string = '1w';
    let bestConfidence = 0;

    for (const [horizon, result] of Object.entries(horizonResults)) {
      const typedResult = result as any;
      if (typedResult.success && typedResult.analysis) {
        const weight = horizonWeights[horizon as keyof typeof horizonWeights];
        const score = typedResult.analysis.confidence || 50;

        totalScore += score * weight;
        totalWeight += weight;

        if (typedResult.analysis.confidence > bestConfidence) {
          bestConfidence = typedResult.analysis.confidence;
          bestHorizon = horizon;
        }
      }
    }

    const overallScore = totalWeight > 0 ? totalScore / totalWeight : 50;

    // Determine overall action
    let action: 'BUY' | 'SELL' | 'HOLD' | 'STRONG_BUY' | 'STRONG_SELL';
    if (overallScore >= 75) {
      action = 'STRONG_BUY';
    } else if (overallScore >= 60) {
      action = 'BUY';
    } else if (overallScore >= 40) {
      action = 'HOLD';
    } else if (overallScore >= 25) {
      action = 'SELL';
    } else {
      action = 'STRONG_SELL';
    }

    return {
      action,
      confidence: overallScore,
      reasoning: `Multi-horizon analysis shows ${action} recommendation with ${overallScore.toFixed(1)}% confidence. Best performing horizon: ${bestHorizon}`,
      timeHorizon: bestHorizon,
      riskLevel: 'MEDIUM' as const,
    };
  }

  /**
   * Calculate overall data quality from horizon results
   */
  private calculateOverallDataQuality(horizonResults: any): number {
    const qualities = Object.values(horizonResults)
      .filter((result: any) => result.success && result.analysis)
      .map((result: any) => (result as any).analysis.dataQuality || 0);

    if (qualities.length === 0) return 0;
    return (
      qualities.reduce((sum: number, quality: number) => sum + quality, 0) /
      qualities.length
    );
  }

  /**
   * Calculate total data points from horizon results
   */
  private calculateTotalDataPoints(horizonResults: any): number {
    return Object.values(horizonResults)
      .filter((result: any) => result.success && result.analysis)
      .reduce(
        (sum: number, result: any) => sum + (result.analysis.dataPoints || 0),
        0,
      );
  }
}

export const comprehensiveAnalysisService = new ComprehensiveAnalysisService();
export default comprehensiveAnalysisService;
